import { useState } from 'react'
import { GitCommitHorizontal, Tag, Zap, Bug, Shield, Plus, Search } from 'lucide-react'

const LOGS = [
  {
    version: 'v2.4.1', date: 'May 6, 2025', tag: 'patch',
    changes: [
      { type: 'fix',      text: 'Fixed invoice PDF download on Safari browsers' },
      { type: 'fix',      text: 'Resolved user search returning incorrect results' },
      { type: 'security', text: 'Patched XSS vulnerability in feedback form' },
    ],
  },
  {
    version: 'v2.4.0', date: 'Apr 20, 2025', tag: 'minor',
    changes: [
      { type: 'feature',  text: 'Added Email Center with template management' },
      { type: 'feature',  text: 'Subscription plans now support annual billing' },
      { type: 'improve',  text: 'Dashboard analytics charts now support dark mode' },
      { type: 'fix',      text: 'Sub-admin role permissions now correctly enforced' },
    ],
  },
  {
    version: 'v2.3.0', date: 'Mar 31, 2025', tag: 'minor',
    changes: [
      { type: 'feature',  text: 'Invoice module with PDF export and print support' },
      { type: 'feature',  text: 'Push notifications now support scheduled delivery' },
      { type: 'improve',  text: 'System logs UI redesigned with better filtering' },
      { type: 'security', text: 'JWT token expiry reduced to 24 hours for security' },
    ],
  },
  {
    version: 'v2.2.0', date: 'Mar 10, 2025', tag: 'minor',
    changes: [
      { type: 'feature',  text: 'Support ticket system with priority management' },
      { type: 'improve',  text: 'User table now supports bulk actions' },
      { type: 'fix',      text: 'Transaction filters not persisting on page reload' },
    ],
  },
  {
    version: 'v2.1.0', date: 'Feb 18, 2025', tag: 'minor',
    changes: [
      { type: 'feature',  text: 'Analytics page with revenue and user growth charts' },
      { type: 'feature',  text: 'Report export to CSV and PDF added' },
      { type: 'improve',  text: 'Sidebar now collapses on mobile automatically' },
      { type: 'fix',      text: 'Admin profile picture upload fixed on Windows' },
    ],
  },
  {
    version: 'v2.0.0', date: 'Jan 30, 2025', tag: 'major',
    changes: [
      { type: 'feature',  text: 'Full dashboard redesign with new dark/light theme' },
      { type: 'feature',  text: 'Multi-admin support with sub-admin roles' },
      { type: 'feature',  text: 'Item management with stock tracking' },
      { type: 'security', text: 'Complete auth system overhaul with 2FA support' },
      { type: 'improve',  text: 'Performance improved by 60% across all pages' },
    ],
  },
]

const TYPE_META = {
  feature:  { label: 'Feature',  color: '#4F46E5', bg: 'rgba(79,70,229,0.12)',  border: 'rgba(79,70,229,0.25)',  Icon: Zap },
  improve:  { label: 'Improved', color: '#06B6D4', bg: 'rgba(6,182,212,0.12)',  border: 'rgba(6,182,212,0.25)',  Icon: GitCommitHorizontal },
  fix:      { label: 'Fix',      color: '#10B981', bg: 'rgba(16,185,129,0.12)', border: 'rgba(16,185,129,0.25)', Icon: Bug },
  security: { label: 'Security', color: '#EF4444', bg: 'rgba(239,68,68,0.12)',  border: 'rgba(239,68,68,0.25)',  Icon: Shield },
}

const TAG_META = {
  major: { color: '#A855F7', bg: 'rgba(168,85,247,0.12)', border: 'rgba(168,85,247,0.25)', label: 'Major' },
  minor: { color: '#4F46E5', bg: 'rgba(79,70,229,0.12)',  border: 'rgba(79,70,229,0.25)',  label: 'Minor' },
  patch: { color: '#10B981', bg: 'rgba(16,185,129,0.12)', border: 'rgba(16,185,129,0.25)', label: 'Patch' },
}

const stats = [
  { label: 'Total Releases', value: LOGS.length,                                        color: '#4F46E5', glow: 'rgba(79,70,229,0.18)',  variant: 'indigo', trend: 'all time',  Icon: Tag },
  { label: 'Features Added', value: LOGS.flatMap(l => l.changes).filter(c => c.type === 'feature').length,  color: '#06B6D4', glow: 'rgba(6,182,212,0.15)',  variant: 'cyan',   trend: 'this year', Icon: Zap },
  { label: 'Bugs Fixed',     value: LOGS.flatMap(l => l.changes).filter(c => c.type === 'fix').length,      color: '#10B981', glow: 'rgba(16,185,129,0.15)', variant: 'green',  trend: 'resolved',  Icon: Bug },
  { label: 'Security Fixes', value: LOGS.flatMap(l => l.changes).filter(c => c.type === 'security').length, color: '#EF4444', glow: 'rgba(239,68,68,0.15)',  variant: 'red',    trend: 'patches',   Icon: Shield },
]

export default function Changelog() {
  const [search, setSearch] = useState('')
  const [filterTag, setFilterTag] = useState('')

  const filtered = LOGS.filter(l =>
    (!filterTag || l.tag === filterTag) &&
    (!search || l.version.toLowerCase().includes(search.toLowerCase()) ||
      l.changes.some(c => c.text.toLowerCase().includes(search.toLowerCase())))
  )

  return (
    <div className="dash-page page-in">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--space-md)' }}>
        <h1 className="page-title">Changelog</h1>
        <button className="btn btn-primary"><Plus size={14} /> New Release</button>
      </div>

      {/* Stats */}
      <div className="stats-grid cols-4">
        {stats.map(s => (
          <div key={s.label} className={`stat-card stat-card--${s.variant}`}>
            <span style={{ position: 'absolute', top: 0, right: 0, width: 110, height: 110, borderRadius: '50%', background: s.glow, filter: 'blur(30px)', transform: 'translate(30%,-30%)', pointerEvents: 'none' }} />
            <div className="stat-card-header">
              <div className="stat-card-icon" style={{ background: s.glow, color: s.color, border: `1px solid ${s.glow}` }}>
                <s.Icon size={15} strokeWidth={1.8} />
              </div>
              <span className="stat-card-trend" style={{ color: s.color, background: `${s.color}15`, border: `1px solid ${s.glow}` }}>{s.trend}</span>
            </div>
            <div className="stat-card-value">{s.value}</div>
            <div className="stat-card-label">{s.label}</div>
            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg,${s.color}88,transparent)`, borderRadius: '0 0 var(--radius-lg) var(--radius-lg)' }} />
          </div>
        ))}
      </div>

      {/* Table card */}
      <div className="card" style={{ padding: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', padding: 'var(--space-md) var(--space-lg)', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
            <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)' }} />
            <input className="input" style={{ paddingLeft: 32 }} placeholder="Search versions or changes…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <select className="input" style={{ width: 'auto', minWidth: 130 }} value={filterTag} onChange={e => setFilterTag(e.target.value)}>
            <option value="">All Types</option>
            <option value="major">Major</option>
            <option value="minor">Minor</option>
            <option value="patch">Patch</option>
          </select>
        </div>

        {/* Timeline */}
        <div style={{ padding: 'var(--space-lg)', display: 'flex', flexDirection: 'column', gap: 'var(--space-lg)' }}>
          {filtered.map((log, idx) => {
            const tm = TAG_META[log.tag]
            return (
              <div key={log.version} style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 'var(--space-lg)', alignItems: 'start' }}>
                {/* Left: version + date */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 2 }}>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-md)', fontWeight: 700, color: 'var(--text1)' }}>{log.version}</span>
                  <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text3)' }}>{log.date}</span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', width: 'fit-content', padding: '2px 10px', borderRadius: 'var(--radius-full)', fontSize: 'var(--fs-xs)', fontWeight: 700, background: tm.bg, color: tm.color, border: `1px solid ${tm.border}` }}>
                    {tm.label}
                  </span>
                </div>

                {/* Right: changes */}
                <div style={{ background: 'var(--bg-raised)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
                  {log.changes.map((c, i) => {
                    const cm = TYPE_META[c.type]
                    return (
                      <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 16px', borderBottom: i < log.changes.length - 1 ? '1px solid var(--border-subtle)' : 'none' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 'var(--radius-full)', fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap', background: cm.bg, color: cm.color, border: `1px solid ${cm.border}`, flexShrink: 0, marginTop: 2 }}>
                          <cm.Icon size={10} strokeWidth={2} />{cm.label}
                        </span>
                        <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--text2)', lineHeight: 1.6 }}>{c.text}</span>
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}

          {filtered.length === 0 && (
            <div style={{ textAlign: 'center', padding: 'var(--space-xl)', color: 'var(--text3)', fontSize: 'var(--fs-sm)' }}>
              No releases match your search.
            </div>
          )}
        </div>
      </div>

      <style>{`
        .dash-page { display:flex; flex-direction:column; gap:var(--space-lg); width:100%; }
        @media (max-width: 640px) {
          .cl-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  )
}